import { useState } from "react";
import { Upload, FileText, Download, Trash2, Loader2, Eye, ChevronDown, ChevronUp, CheckCircle, Clock, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/lib/constants";

const DOC_TYPE_COLORS = {
  proposal: "bg-purple-50 text-purple-700 border-purple-200",
  invoice: "bg-blue-50 text-blue-700 border-blue-200",
  receipt: "bg-green-50 text-green-700 border-green-200",
  contract: "bg-amber-50 text-amber-700 border-amber-200",
  general: "bg-slate-50 text-slate-700 border-slate-200",
};

const STATUS_ICON = {
  complete: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
  analyzing: <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />,
  pending: <Clock className="w-3.5 h-3.5 text-slate-400" />,
  failed: <AlertTriangle className="w-3.5 h-3.5 text-red-500" />,
};

export function DocumentsSection({ documents, clientId, onRefresh, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large. Max 25MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("client_id", clientId);
    formData.append("document_type", inferDocType(file.name));
    formData.append("auto_analyze", "true");

    try {
      const res = await fetch(`${API}/client-documents/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Uploaded ${file.name}. ${data.analysis?.success ? "AI analysis complete!" : "Queued for analysis."}`);
        onRefresh();
      } else {
        toast.error(data.error || "Upload failed");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAnalyze = async (docId) => {
    setAnalyzing(docId);
    try {
      const res = await fetch(`${API}/client-documents/${docId}/analyze`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Document analyzed successfully!");
        onRefresh();
      } else {
        toast.error(data.error || "Analysis failed");
      }
    } catch {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const res = await fetch(`${API}/client-documents/${docId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Document deleted");
        onRefresh();
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  const inferDocType = (filename) => {
    const lower = filename.toLowerCase();
    if (lower.includes("invoice") || lower.includes("inv")) return "invoice";
    if (lower.includes("proposal") || lower.includes("prop")) return "proposal";
    if (lower.includes("receipt") || lower.includes("rcpt")) return "receipt";
    if (lower.includes("contract") || lower.includes("agreement")) return "contract";
    return "general";
  };

  return (
    <Card className="border-slate-200" data-testid="documents-section">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
          <FileText className="w-5 h-5 text-indigo-600" />
          Documents
          {documents.length > 0 && (
            <Badge variant="secondary" className="ml-1 text-xs">{documents.length}</Badge>
          )}
        </CardTitle>
        {canEdit && (
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="document-upload-input"
            />
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={uploading}
              asChild
              data-testid="upload-document-btn"
            >
              <span>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {uploading ? "Uploading..." : "Upload"}
              </span>
            </Button>
          </label>
        )}
      </CardHeader>
      <CardContent>
        {documents.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No documents uploaded yet.</p>
            <p className="text-xs mt-1">Upload proposals, invoices, receipts, or contracts.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.document_id}
                className="border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`doc-${doc.document_id}`}
              >
                {/* Document Header */}
                <div
                  className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => setExpandedDoc(expandedDoc === doc.document_id ? null : doc.document_id)}
                >
                  <div className="flex-shrink-0">
                    {STATUS_ICON[doc.analysis_status] || STATUS_ICON.pending}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{doc.original_filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${DOC_TYPE_COLORS[doc.document_type] || DOC_TYPE_COLORS.general}`}>
                        {doc.document_type}
                      </Badge>
                      <span className="text-[10px] text-slate-400">
                        {new Date(doc.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                      </span>
                      {doc.uploaded_by_name && (
                        <span className="text-[10px] text-slate-400">by {doc.uploaded_by_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {doc.analysis_status === "pending" && canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-blue-600"
                        onClick={(e) => { e.stopPropagation(); handleAnalyze(doc.document_id); }}
                        disabled={analyzing === doc.document_id}
                        data-testid={`analyze-doc-${doc.document_id}`}
                      >
                        {analyzing === doc.document_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                      </Button>
                    )}
                    <a
                      href={`${API}/client-documents/download/${doc.document_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-slate-100 text-slate-500"
                      data-testid={`download-doc-${doc.document_id}`}
                    >
                      <Download className="w-3 h-3" />
                    </a>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                        onClick={(e) => { e.stopPropagation(); handleDelete(doc.document_id); }}
                        data-testid={`delete-doc-${doc.document_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {expandedDoc === doc.document_id ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Insights */}
                {expandedDoc === doc.document_id && doc.extracted_data && (
                  <div className="border-t border-slate-100 p-3 bg-slate-50 space-y-3 text-sm">
                    {doc.extracted_data.summary && (
                      <p className="text-slate-700">{doc.extracted_data.summary}</p>
                    )}

                    {/* Financial Info */}
                    {doc.extracted_data.financial_info && (
                      <div className="bg-white rounded-lg p-2.5 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Financial Details</p>
                        {doc.extracted_data.financial_info.total_value && (
                          <p className="font-semibold text-slate-800">
                            {doc.extracted_data.financial_info.currency || "NGN"} {Number(doc.extracted_data.financial_info.total_value).toLocaleString()}
                          </p>
                        )}
                        {doc.extracted_data.financial_info.payments?.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs mt-1.5 text-slate-600">
                            <span>{p.description || p.category}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{Number(p.amount).toLocaleString()}</span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0">
                                {p.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {doc.extracted_data.financial_info.payment_terms && (
                          <p className="text-xs text-slate-500 mt-1">Terms: {doc.extracted_data.financial_info.payment_terms}</p>
                        )}
                      </div>
                    )}

                    {/* Engagement Info */}
                    {doc.extracted_data.engagement_info && (
                      <div className="bg-white rounded-lg p-2.5 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Engagement</p>
                        {doc.extracted_data.engagement_info.engagement_type && (
                          <p className="text-slate-800 font-medium">{doc.extracted_data.engagement_info.engagement_type}</p>
                        )}
                        {doc.extracted_data.engagement_info.scope && (
                          <p className="text-xs text-slate-600 mt-0.5">{doc.extracted_data.engagement_info.scope}</p>
                        )}
                        {doc.extracted_data.engagement_info.duration && (
                          <p className="text-xs text-slate-500 mt-0.5">Duration: {doc.extracted_data.engagement_info.duration}</p>
                        )}
                      </div>
                    )}

                    {/* Contacts Found */}
                    {doc.extracted_data.client_info?.contacts?.length > 0 && (
                      <div className="bg-white rounded-lg p-2.5 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Contacts Found</p>
                        {doc.extracted_data.client_info.contacts.map((c, i) => (
                          <p key={i} className="text-xs text-slate-700">
                            <span className="font-medium">{c.name}</span>
                            {c.role && <span className="text-slate-500"> — {c.role}</span>}
                            {c.email && <span className="text-blue-600 ml-1">{c.email}</span>}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Next Actions */}
                    {doc.extracted_data.next_actions?.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                        <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Suggested Actions</p>
                        {doc.extracted_data.next_actions.map((a, i) => (
                          <p key={i} className="text-xs text-amber-800">• {a}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Failed Analysis */}
                {expandedDoc === doc.document_id && doc.analysis_status === "failed" && (
                  <div className="border-t border-red-100 p-3 bg-red-50 text-sm text-red-700">
                    Analysis failed. {canEdit && (
                      <Button
                        size="sm"
                        variant="link"
                        className="text-red-700 p-0 h-auto"
                        onClick={() => handleAnalyze(doc.document_id)}
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
